import {map} from 'lit/directives/map.js';
import {when} from 'lit/directives/when.js';
import {connect} from 'pwa-helpers';
import {LitElement, html} from 'lit';
import {customElement} from 'lit/decorators/custom-element.js';
import {msg, str} from '@lit/localize';
import {BILLING_ANNUALLY, BILLING_MONTHLY} from '../../services/subscription/billingCycle';
import {SUB_ACTIVE, SUB_TRIALING} from '../../services/subscription/subscriptionStatus';
import {openDialog} from '../../slices/statusSlice';
import {updateSubscription} from '../../slices/accountSlice';
import paddle from '../../services/subscription/paddleService';
import {
	selectLanguage,
	selectPlans,
	selectSubscribedPlan,
	selectSubscribedPrice,
	selectPrices,
	selectCurrentBillingCycle,
	selectCurrentSubscription,
	selectIsTrialing,
} from '../../selectors';
import store from '../../store';
import style from '../../../../scss/choosePlan.scss';
import {changeSubscription} from '../../slices/planSlice';
import {getValueForLanguage} from '../../util/getValueForLanguage';

@customElement('arc-choose-plan')
export class ChoosePlan extends connect(store)(LitElement) {
	static styles = [style];

	static properties = {
		plans: {type: Array},
		subscribedPlan: {type: Object},
		subscribedPrice: {type: Object},
		lang: {type: String},
		accountData: {type: Object},
		selectedBillingCycle: {type: String},
		prices: {type: Array},
		dashboardLink: {type: String},
		isTrialing: {type: Boolean},
		paymentProvider: {type: String},
		showSEPACheckout: {type: Boolean},
	};

	constructor() {
		super();
		this.plans = [];
		this.subscription = null;
		this.subscribedPlan = null;
		this.subscribedPrice = null;
		this.lang = 'en';
		this.accountData = null;
		this.selectedBillingCycle = null;
		this.selectedPlan = null;
		this.dashboardLink = null;
		this.allowDowngrades = false;
		this.isTrialing = false;
		this.prices = [];
		this.paymentProvider = 'manual';
		this.showSEPACheckout = false;
	}

	stateChanged(state) {
		this.plans = selectPlans(state);
		this.subscription = selectCurrentSubscription(state);
		this.subscribedPlan = selectSubscribedPlan(state);
		this.subscribedPrice = selectSubscribedPrice(state);
		this.isTrialing = selectIsTrialing(state);
		this.prices = selectPrices(state);
		this.lang = selectLanguage(state);
		this.selectedBillingCycle = this.selectedBillingCycle ?? selectCurrentBillingCycle(state);
		this.accountData = state.account.data.account;
		this.dashboardLink = state.plan.dashboardLink;
	}

	onPaddleCheckoutEvent(ev) {
		if (ev.name === 'checkout.completed' && this.selectedPlan) {
			// put provisional subscription data in store
			const trialPeriod = ev.data.items[0].trial_period;
			const status = trialPeriod ? SUB_TRIALING : SUB_ACTIVE;
			let trialEndsAt = null;
			if (trialPeriod) {
				const end = new Date();
				end.setDate(end.getDate() + trialPeriod.frequency);
				trialEndsAt = `${end.getFullYear()}-${
					end.getMonth() + 1
				}-${end.getDate()} ${end.getHours()}:${end.getMinutes()}:${end.getSeconds()}`;
			}
			store.dispatch(
				updateSubscription({
					trialEndsAt: trialEndsAt,
					status: status,
					plan: this.selectedPlan,
					price: this.selectedPlan.prices.find((p) => p.billingCycle === this.selectedBillingCycle),
				})
			);
		}
	}

	async connectedCallback() {
		super.connectedCallback();
		this.allowDowngrades = store.getState().status.allowDowngrades;
		if (this.paymentProvider === 'paddle') {
			await paddle.initialize(
				store.getState().status.paddleToken,
				this.lang,
				this.onPaddleCheckoutEvent.bind(this)
			);
		}
	}

	selectPlan(plan, alternateBillingCycle) {
		this.selectedPlan = plan;
		const price = plan.prices.find((p) => p.billingCycle === this.selectedBillingCycle);
		if (this.subscribedPrice && !this.subscribedPlan.free) {
			// change existing subscription
			// we could be upgrading, changing billing cycle, or, possibly downgrading
			const planName = getValueForLanguage(plan.longName, this.lang);
			const billingCycleLabel = (
				this.selectedBillingCycle === BILLING_MONTHLY
					? msg('billing_cycle_monthly')
					: msg('billing_cycle_annually')
			).toLowerCase();
			const dialogOptions = {
				title: msg('Upgrade'),
				message: msg(
					str`You are about to change your subscription to ${planName}. The new ${billingCycleLabel} price will be ${
						price.price / 100
					}€.`
				),
				labelConfirm: msg('OK'),
				labelCancel: msg('Cancel'),
			};
			if (alternateBillingCycle) {
				dialogOptions.title = msg('Change Billing Cycle');
				dialogOptions.message = msg(
					str`You are about to change the billing cycle for your subscription. The new ${billingCycleLabel} price will be ${
						price.price / 100
					}€.`
				);
			}
			const handleConfirm = async () => {
				store.dispatch(changeSubscription({priceId: price.id}));
				window.removeEventListener('confirm', handleConfirm);
			};
			window.addEventListener('confirm', handleConfirm);
			store.dispatch(openDialog(dialogOptions));
		} else {
			// open checkout for new subscription
			if (this.paymentProvider === 'paddle') {
				paddle.openCheckout(price.providerId, this.accountData.email, {
					aaki_account_id: this.accountData.id,
					aaki_plan_id: plan.id,
					aaki_price_id: price.id,
				});
			}
			if (this.paymentProvider === 'manual') {
				this.showSEPACheckout = true;
			}
		}
	}

	derivePrice(plan) {
		if (!plan) return {price: 0};
		let price = plan.prices.find((p) => p.billingCycle === this.selectedBillingCycle);
		if (!price) {
			price = {price: 0};
		}
		if (this.selectedBillingCycle === BILLING_ANNUALLY) {
			price = {...price, price: price.price / 12}; // show annual prices as monthly prices
		}
		return price;
	}

	isAlternateBillingCycle(plan) {
		const isCurrentCycle = this.selectedBillingCycle === this.subscribedPrice?.billingCycle;
		return plan.isCurrent && !isCurrentCycle && !plan.free;
	}

	render() {
		const billingCycleOptions = [
			{value: BILLING_MONTHLY, label: msg('billing_cycle_monthly')},
			{value: BILLING_ANNUALLY, label: msg('billing_cycle_annually')},
		];
		const types = {
			'1_cat_1_artist': msg('plan_type_1_cat_1_artist'),
			'1_cat_many_artists': msg('plan_type_1_cat_many_artists'),
			many_cats_many_artists: msg('plan_type_many_cats_many_artists'),
			custom: msg('plan_type_custom'),
		};
		return html`
			<section class="choosePlanChoice">
				<div class="choosePlanBillingCycle">
					<arc-switch
						title="${msg('choose_billing_cycle_title')}"
						.tooltip=${msg('choose_billing_cycle_title_tip')}
						.options=${billingCycleOptions}
						.value=${this.selectedBillingCycle}
						@switch-change=${(e) => {
							this.selectedBillingCycle = e.detail.value;
						}}
					></arc-switch>
				</div>
				<div class="choosePlanTableScroller">
					<table class="choosePlanTable">
						<thead>
						<tr>
							<th></th>
							${map(
								this.plans,
								(plan) => html`
									<th>
										<h4>${getValueForLanguage(plan.name, this.lang)}</h4>
										<p>
											${when(
												plan.priceOnRequest,
												() => html`<span>${msg('choose_plan_price_on_request')}</span>`,
												() => this.derivePrice(plan).price / 100 + ' €'
											)}
										</p>
									</th>`
							)}
						</tr>
						<tr>
							<th></th>
							${map(
								this.plans,
								(plan) => html`
									<th>
										<arc-choose-plan-button
											.plan=${plan}
											.currentPlan=${this.subscribedPlan}
											?alternateBillingCycle=${this.isAlternateBillingCycle(plan)}
											.onSelect=${this.selectPlan.bind(this)}
										></arc-choose-plan-button>
									</th>`
							)}
						</tr>
						</thead>
						<tbody>
						<tr class="primary-section">
							<td><h4>${msg('plan_type')}</h4></td>
							${map(this.plans, (plan) => html`
								<td>${types[plan.features.type]}</td>`)}
						</tr>
						<tr class="primary-section">
							<td><h4>${msg('plan_max_artworks')}</h4></td>
							${map(this.plans, (plan) => html`
								<td>${msg('plan_max_artworks_unlimited')}</td>`)}
						</tr>
						<tr>
							<td><h4>${msg('plan_media_per_work')}</h4></td>
							${map(
								this.plans,
								(plan) => html`
									<td>
										${!isNaN(plan.features.mediaPerWork)
											? plan.features.mediaPerWork
											: msg('plan_type_custom')}
									</td>`
							)}
						</tr>
						<tr>
							<td><h4>${msg('plan_max_filesizes')}</h4></td>
							${map(
								this.plans,
								(plan) => html`
									<td>
										${Array.isArray(plan.features.filesizes)
											? plan.features.filesizes.join(' / ') + ' MB'
											: msg('plan_type_custom')}
									</td>`
							)}
						</tr>
						<tr>
							<td><h4>${msg('plan_max_storage')}</h4></td>
							${map(
								this.plans,
								(plan) => html`
									<td>
										${!isNaN(plan.features.storageSpace)
											? plan.features.storageSpace + 'GB'
											: msg('plan_type_custom')}
									</td>`
							)}
						</tr>
						<tr>
							<td><h4>${msg('plan_max_team_size')}</h4></td>
							${map(this.plans, (plan) => {
								let teamSize = '-';
								if (!isNaN(plan.features.teamSize)) {
									if (plan.features.teamSize > 1) {
										teamSize = plan.features.teamSize;
									}
								} else {
									teamSize = msg('plan_type_custom');
								}
								return html`
									<td>${teamSize}</td>`;
							})}
						</tr>
						<tr>
							<td><h4>${msg('plan_max_support')}</h4></td>
							${map(
								this.plans,
								() => html`
									<td class="check">✓</td>`
							)}
						</tr>
						</tbody>
						<tfoot>
						<tr>
							<th></th>
							${map(
								this.plans,
								(plan) => html`
									<th>
										<arc-choose-plan-button
											.plan=${plan}
											.currentPlan=${this.subscribedPlan}
											?alternateBillingCycle=${this.isAlternateBillingCycle(plan)}
											.onSelect=${this.selectPlan.bind(this)}
										></arc-choose-plan-button>
									</th>`
							)}
						</tr>
						</tfoot>
					</table>
					<!-- pass the price – we need to send that to the server -->
					<arc-sepa-checkout
						.show=${this.showSEPACheckout}
						.planName=${getValueForLanguage(this.selectedPlan?.name, this.lang)}
						.price=${this.derivePrice(this.selectedPlan)}
						.billingCycle=${this.selectedBillingCycle}
						.onClose=${(ev) => (this.showSEPACheckout = false)}
					>
					</arc-sepa-checkout>
				</div>
			</section>
		`;
	}
}
